import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Plus,
  AlertCircle,
  Trash2,
  ShieldCheck,
  Clock,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { relativeTime } from '@/lib/format';

export function SettingsAccountsPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Surface OAuth callback status from query params.
  useEffect(() => {
    const status = params.get('status');
    const detail = params.get('detail');
    if (!status) return;
    if (status === 'connected') {
      toast.success(`Connected ${detail ?? 'account'}.`);
      void queryClient.invalidateQueries({ queryKey: qk.accounts });
    } else if (status === 'error') {
      toast.error(`Could not connect: ${detail ?? 'unknown error'}.`);
    }
    setParams({}, { replace: true });
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: qk.accounts,
    queryFn: () => api.listAccounts(),
  });

  const clientsQuery = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: () => api.listOAuthClients(),
  });
  const clients = clientsQuery.data?.clients ?? [];
  const clientByKey = new Map(clients.map((c) => [c.key, c]));

  const calendarStatusQuery = useQuery({
    queryKey: qk.calendarStatus,
    queryFn: () => api.listCalendarAccountStatuses(),
  });
  const calendarStatusByAccount = new Map(
    (calendarStatusQuery.data?.accounts ?? []).map((s) => [s.accountId, s]),
  );

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Settings
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Email accounts</h1>
            <p className="text-sm text-muted-foreground">
              Connect the Gmail accounts you want triaged. Sync runs every 5 minutes per account.
              Tokens are encrypted at rest with AES-256-GCM.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {clients.map((c) => (
              <a key={c.key} href={`/api/google/oauth/start?client=${c.key}`}>
                <Button variant={c.internal ? 'default' : 'outline'} size="sm">
                  <Plus className="h-3.5 w-3.5" />
                  Connect {c.displayName}
                  {c.internal ? (
                    <ShieldCheck className="ml-1 h-3 w-3 opacity-80" />
                  ) : (
                    <Clock className="ml-1 h-3 w-3 opacity-80" />
                  )}
                </Button>
              </a>
            ))}
          </div>
        </div>
        {clients.length > 0 && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            <ShieldCheck className="mr-1 inline-block h-3 w-3" /> Internal client (permanent
            tokens) ·{' '}
            <Clock className="mr-1 inline-block h-3 w-3" /> External client (reconnect every 7
            days until verified)
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : data?.accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
              <Mail className="h-5 w-5" />
            </div>
            <p className="text-sm">No Gmail accounts connected yet.</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Click <em>Connect Gmail account</em> above. Google will ask you to grant access to
              read, compose, and send mail. You'll repeat this once per inbox.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {data?.accounts.map((a) => (
                <li key={a.id} className="flex items-center gap-4 p-4">
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium">{a.email}</p>
                      {a.oauthClientKey && clientByKey.get(a.oauthClientKey) && (
                        <Badge variant={clientByKey.get(a.oauthClientKey)!.internal ? 'success' : 'warning'}>
                          {clientByKey.get(a.oauthClientKey)!.internal ? (
                            <ShieldCheck className="mr-1 h-3 w-3" />
                          ) : (
                            <Clock className="mr-1 h-3 w-3" />
                          )}
                          {clientByKey.get(a.oauthClientKey)!.displayName}
                        </Badge>
                      )}
                      {a.lastSyncError && (
                        <Badge variant="danger">
                          <AlertCircle className="mr-1 h-3 w-3" /> sync error
                        </Badge>
                      )}
                      {!a.hasHistoryId && !a.lastSyncError && (
                        <Badge variant="warning">awaiting first sync</Badge>
                      )}
                      {a.hasHistoryId && !a.lastSyncError && (
                        <Badge variant="success">syncing</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {a.messageCount} messages · last sync {relativeTime(a.lastSyncAt)}
                    </p>
                    {a.lastSyncError && (
                      <p className="mt-1 text-[11px] text-destructive">
                        {a.lastSyncError}
                      </p>
                    )}
                    <CalendarRow
                      accountId={a.id}
                      email={a.email}
                      oauthClientKey={a.oauthClientKey}
                      status={calendarStatusByAccount.get(a.id)}
                    />
                  </div>
                  <DisconnectButton id={a.id} email={a.email} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DisconnectButton({ id, email }: { id: string; email: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: () => api.disconnectAccount(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.accounts });
      toast.success(`Disconnected ${email}.`);
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to disconnect.');
      setOpen(false);
    },
  });
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Trash2 className="h-3.5 w-3.5 text-destructive" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {email}?</DialogTitle>
            <DialogDescription>
              This removes the encrypted tokens and all synced messages for this account. You'll
              need to reconnect via OAuth to resume. Project activities derived from these
              messages survive (loose foreign keys).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CalendarRow({
  accountId,
  email,
  oauthClientKey,
  status,
}: {
  accountId: string;
  email: string;
  oauthClientKey: string | null;
  status: import('@cc/types').CalendarAccountStatusDto | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  // Re-uses /api/google/oauth/start since the calendar scope is now included
  // in GMAIL_SCOPES; passing hint pre-selects this email in Google's chooser
  // so a re-consent click flips the persisted scopes array on this account row.
  const connectHref = oauthClientKey
    ? `/api/google/oauth/start?client=${oauthClientKey}&hint=${encodeURIComponent(email)}`
    : null;

  if (!status || !status.hasCalendarScope) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-[10px]">
          <CalendarIcon className="mr-1 h-3 w-3" /> Calendar: not connected
        </Badge>
        {connectHref && (
          <a href={connectHref}>
            <Button variant="outline" size="sm" className="h-6 text-[11px]">
              <Plus className="h-3 w-3" /> Connect calendar
            </Button>
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="success" className="text-[10px]">
          <CalendarIcon className="mr-1 h-3 w-3" /> Calendar: connected
        </Badge>
        <span className="text-[11px] text-muted-foreground">
          {status.primaryCalendarSummary
            ? `${status.primaryCalendarSummary} · last sync ${relativeTime(status.lastSyncAt)}`
            : `last sync ${relativeTime(status.lastSyncAt)}`}
        </span>
        {status.lastSyncError && (
          <span className="inline-flex items-center gap-1 text-[11px] text-destructive">
            <AlertCircle className="h-3 w-3" /> {status.lastSyncError}
          </span>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {expanded ? 'Hide' : 'Show'} other calendars
        </button>
      </div>
      {expanded && <CalendarPicker accountId={accountId} />}
    </div>
  );
}

function CalendarPicker({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.accountCalendars(accountId),
    queryFn: () => api.listAccountCalendars(accountId),
  });
  const toggleMutation = useMutation({
    mutationFn: (args: { googleCalendarId: string; syncEnabled: boolean }) =>
      api.setAccountCalendarSync(accountId, args.googleCalendarId, args.syncEnabled),
    onSuccess: () => {
      toast.success('Updated.');
      void queryClient.invalidateQueries({ queryKey: qk.accountCalendars(accountId) });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not update calendar.'),
  });

  if (isLoading) return <Skeleton className="h-10" />;
  if (isError || !data) {
    return <p className="text-[11px] text-destructive">Could not list calendars.</p>;
  }
  if (data.calendars.length === 0) {
    return <p className="text-[11px] text-muted-foreground">No calendars visible.</p>;
  }
  return (
    <ul className="space-y-1 rounded-md border border-border bg-muted/20 p-2">
      {data.calendars.map((c) => (
        <li key={c.googleCalendarId} className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 flex-none rounded-full"
              style={{ backgroundColor: c.backgroundColor ?? '#888' }}
            />
            <div className="min-w-0">
              <p className="truncate text-xs">
                {c.summary}
                {c.isPrimary && (
                  <Badge variant="outline" className="ml-1.5 text-[9px]">primary</Badge>
                )}
                {c.accessRole === 'reader' && !c.isPrimary && (
                  <Badge variant="outline" className="ml-1.5 text-[9px]">read-only</Badge>
                )}
              </p>
              {c.lastSyncAt && (
                <p className="truncate text-[10px] text-muted-foreground">
                  last sync {relativeTime(c.lastSyncAt)}
                </p>
              )}
            </div>
          </div>
          <label className="inline-flex flex-none items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              checked={c.syncEnabled}
              disabled={c.isPrimary || toggleMutation.isPending}
              onChange={(e) =>
                toggleMutation.mutate({
                  googleCalendarId: c.googleCalendarId,
                  syncEnabled: e.target.checked,
                })
              }
            />
            sync
          </label>
        </li>
      ))}
    </ul>
  );
}
